import React, { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Users, BarChart3, ClipboardList, Wallet, Settings as SettingsIcon,
  LogOut, Loader2, Menu, X, TrendingUp, ScrollText,
} from "lucide-react";
import { adminAxios } from "./adminApi";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: BarChart3, testId: "admin-nav-dashboard" },
  { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users" },
  { to: "/admin/waitlist", label: "Category Waitlist", icon: ClipboardList, testId: "admin-nav-waitlist" },
  { to: "/admin/ledger", label: "Credit Ledger", icon: Wallet, testId: "admin-nav-ledger" },
  { to: "/admin/scans", label: "Scan Logs", icon: ScrollText, testId: "admin-nav-scans" },
  { to: "/admin/economics", label: "Economics", icon: TrendingUp, testId: "admin-nav-economics" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testId: "admin-nav-settings" },
];

function NavItem({ item, onClick }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={item.testId}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive ? "bg-white/15 text-white font-medium" : "text-white hover:bg-white/[0.08]"
        }`
      }
    >
      <item.icon className="w-4 h-4" strokeWidth={1.8} />
      <span className="flex-1">{item.label}</span>
    </NavLink>
  );
}

export default function AdminShell() {
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await adminAxios().get("/me");
        setAdmin(r.data.admin);
        setReady(true);
      } catch {
        nav("/admin/login");
      }
    })();
  }, [nav]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenuOpen(false);
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);

  const logout = async () => {
    try { await adminAxios().post("/logout"); } catch { /* ignore */ }
    localStorage.removeItem("lm_admin_token");
    toast.success("Signed out");
    nav("/admin/login");
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-lm-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-lm-primary" />
      </div>
    );
  }

  const initial = (admin?.name || admin?.email || "A").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-lm-bg flex" data-testid="admin-shell">
      {/* Sidebar (desktop) — dark, matches AppShell */}
      <aside className="hidden md:flex md:flex-col w-60 border-r border-[color:var(--lm-dark-border)] bg-[color:var(--lm-dark)] text-white fixed top-0 left-0 bottom-0 z-20">
        <div className="h-16 px-5 flex items-center border-b border-[color:var(--lm-dark-border)]">
          <Link to="/" className="flex items-center gap-2" aria-label="Leadmato">
            <Shield className="w-4 h-4 text-white/80" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Leadmato</div>
              <div className="text-sm font-display font-semibold -mt-0.5">Admin</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 pt-3 pb-2 font-mono">
            Control room
          </div>
          {NAV.map((i) => <NavItem key={i.to} item={i} />)}
        </nav>
        <div className="p-3 border-t border-[color:var(--lm-dark-border)] text-[11px] text-white/50 font-mono">
          v1 · admin panel
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-[color:var(--lm-dark)] text-white flex flex-col z-50">
            <div className="h-16 px-5 flex items-center justify-between border-b border-[color:var(--lm-dark-border)]">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-white/80" />
                <span className="font-display font-semibold">Admin</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
              {NAV.map((i) => <NavItem key={i.to} item={i} onClick={() => setMobileOpen(false)} />)}
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="h-16 border-b border-lm-border bg-lm-surface/85 backdrop-blur-md sticky top-0 z-10 flex items-center px-4 md:px-8 gap-4">
          <button
            className="md:hidden -ml-1 p-2 rounded-md hover:bg-lm-tint"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Control room</div>
            <div className="text-sm font-medium text-lm-text truncate">{admin?.email}</div>
          </div>
          <div className="relative" ref={menuRef}>
            <button
              data-testid="admin-user-menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-9 h-9 rounded-full bg-lm-primary text-white font-display font-semibold flex items-center justify-center"
              aria-label="Admin menu"
            >
              {initial}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-lm-surface border border-lm-border rounded-lg shadow-lg py-1 z-30">
                <div className="px-4 py-2 border-b border-lm-border">
                  <div className="text-sm font-medium truncate">{admin?.name || "Admin"}</div>
                  <div className="text-xs text-lm-text3 truncate">{admin?.email}</div>
                </div>
                <NavLink
                  to="/admin/settings"
                  onClick={() => setMenuOpen(false)}
                  className="w-full text-left px-4 py-2 text-sm text-lm-text hover:bg-lm-tint inline-flex items-center gap-2"
                >
                  <SettingsIcon className="w-4 h-4" /> Settings
                </NavLink>
                <button
                  data-testid="admin-logout"
                  onClick={logout}
                  className="w-full text-left px-4 py-2 text-sm text-lm-text hover:bg-lm-tint inline-flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8 lg:p-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
